import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CreditCard, Sparkles } from "lucide-react";

const empty = {
    name: "",
    slug: "",
    description: "",
    price: 4999,
    duration_days: 365,
    benefits: [],
    is_active: true,
    display_order: 0,
};

function PlanEditor({ open, onOpenChange, initial, onSaved }) {
    const isEdit = Boolean(initial?.id);
    const [form, setForm] = useState(empty);
    const [benefitsRaw, setBenefitsRaw] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            const seed = initial ? { ...empty, ...initial } : empty;
            setForm(seed);
            setBenefitsRaw((seed.benefits || []).join("\n"));
        }
    }, [open, initial]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked
                : (["price", "duration_days", "display_order"].includes(name)) ? Number(value)
                : value,
        }));
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: Number(form.price),
                duration_days: Number(form.duration_days),
                display_order: Number(form.display_order),
                benefits: benefitsRaw.split("\n").map((s) => s.trim()).filter(Boolean),
                slug: form.slug?.trim() || undefined,
            };
            if (isEdit) await api.patch(`/admin/membership-plans/${initial.id}`, payload);
            else await api.post("/admin/membership-plans", payload);
            toast.success(isEdit ? "Plan updated" : "Plan created");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="plan-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{isEdit ? "Edit plan" : "New membership plan"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Name</Label>
                            <Input name="name" value={form.name} onChange={onChange} required className="mt-1.5 rounded-xl" data-testid="plan-form-name" />
                        </div>
                        <div>
                            <Label>Slug (auto)</Label>
                            <Input name="slug" value={form.slug || ""} onChange={onChange} placeholder="auto-generated" className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Price (₹)</Label>
                            <Input type="number" name="price" value={form.price} onChange={onChange} required min={0} className="mt-1.5 rounded-xl" data-testid="plan-form-price" />
                        </div>
                        <div>
                            <Label>Duration (days)</Label>
                            <Input type="number" name="duration_days" value={form.duration_days} onChange={onChange} required min={1} max={3650} className="mt-1.5 rounded-xl" data-testid="plan-form-duration" />
                        </div>
                        <div>
                            <Label>Display order</Label>
                            <Input type="number" name="display_order" value={form.display_order} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                        <label className="flex items-end gap-2 text-sm pb-2.5">
                            <input type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} />
                            Active (visible on storefront)
                        </label>
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea name="description" value={form.description} onChange={onChange} rows={2} className="mt-1.5 rounded-xl" />
                    </div>
                    <div>
                        <Label>Benefits (one per line)</Label>
                        <Textarea value={benefitsRaw} onChange={(e) => setBenefitsRaw(e.target.value)} rows={4} placeholder="Unlimited access to all online courses&#10;Priority booking for studio sessions&#10;Member-only events" className="mt-1.5 rounded-xl" data-testid="plan-form-benefits" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="plan-form-save">
                            {saving ? "Saving…" : isEdit ? "Save" : "Create plan"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function MembershipsTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const reload = () => api.get("/admin/membership-plans").then(({ data }) => setItems(data));
    useEffect(() => { reload(); }, []);

    const remove = async (id) => {
        if (!confirm("Delete this plan? Existing memberships stay active until they expire.")) return;
        try {
            await api.delete(`/admin/membership-plans/${id}`);
            toast.success("Plan deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-4" data-testid="memberships-tab">
            <div className="flex justify-between items-end flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-2xl flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-brand-600" /> Membership plans
                    </h3>
                    <p className="text-sm text-ink-700">Annual passes. Courses you flag "Members only" become free for active members.</p>
                </div>
                <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="plan-new-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> New plan
                </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((p) => (
                    <div key={p.id} className="rounded-2xl bg-white border border-gray-100 p-5" data-testid={`plan-row-${p.id}`}>
                        <div className="flex justify-between items-start gap-2 mb-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="h-4 w-4 text-accent-500" />
                                    <span className="font-heading text-lg">{p.name}</span>
                                </div>
                                <div className="text-xs text-ink-700 mt-1">/{p.slug} · {p.duration_days} days {p.is_active ? "" : "· hidden"}</div>
                            </div>
                            <div className="flex gap-1.5 shrink-0">
                                <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`plan-edit-${p.id}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => remove(p.id)} data-testid={`plan-delete-${p.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                        <div className="font-heading text-2xl font-semibold text-ink-900">
                            ₹{Number(p.price).toLocaleString("en-IN")}
                        </div>
                        {p.description && <p className="text-xs text-ink-700 mt-1.5">{p.description}</p>}
                        {p.benefits?.length > 0 && (
                            <ul className="mt-3 space-y-1 text-xs text-ink-700">
                                {p.benefits.slice(0, 4).map((b, i) => (
                                    <li key={i} className="flex items-start gap-1.5">
                                        <Sparkles className="h-3 w-3 mt-0.5 text-accent-500 shrink-0" /> {b}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center py-10 text-ink-700">No plans yet — click "New plan".</div>
                )}
            </div>
            <PlanEditor open={open} onOpenChange={setOpen} initial={editing} onSaved={reload} />
        </div>
    );
}
